import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";

const revealVariants = {
  hidden: { y: "110%" },
  visible: (i = 0) => ({
    y: "0%",
    transition: {
      duration: 1.1,
      delay: 0.35 + i * 0.12,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 1.1 + i * 0.15, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const orb1Y = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const orb2Y = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Background orbs (parallax) */}
      <motion.div
        style={{ y: orb1Y }}
        className="orb absolute -top-40 -left-40 w-[720px] h-[720px] rounded-full bg-[color:var(--accent)]/25 pointer-events-none"
        aria-hidden
      />
      <motion.div
        style={{ y: orb2Y }}
        className="orb absolute -bottom-40 -right-40 w-[620px] h-[620px] rounded-full bg-[color:var(--accent-2)]/25 pointer-events-none"
        aria-hidden
      />

      {/* Grid skeleton overlay */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="h-full w-full grid grid-cols-12">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="border-r hairline last:border-r-0" />
          ))}
        </div>
      </div>

      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-between px-6 md:px-12 pt-32 pb-10"
      >
        {/* Eyebrow */}
        <motion.div
          className="line-mask"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          custom={0}
        >
          <div className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-[color:var(--muted)]">
            <span className="inline-block w-8 h-px bg-[color:var(--accent)]" />
            {t.hero.eyebrow}
          </div>
        </motion.div>

        {/* Kinetic headline */}
        <div className="mt-16 md:mt-24">
          <h1
            className="font-display font-extrabold uppercase text-[16vw] md:text-[13vw] lg:text-[11vw] leading-[0.85] tracking-[-0.04em]"
            data-testid="hero-headline"
          >
            <motion.span
              className="line-mask"
              initial="hidden"
              animate="visible"
            >
              <motion.span variants={revealVariants} custom={0}>
                {t.hero.line1}
              </motion.span>
            </motion.span>
            <motion.span
              className="line-mask text-outline"
              initial="hidden"
              animate="visible"
            >
              <motion.span variants={revealVariants} custom={1}>
                {t.hero.line2}
              </motion.span>
            </motion.span>
            <motion.span
              className="line-mask"
              initial="hidden"
              animate="visible"
            >
              <motion.span variants={revealVariants} custom={2}>
                {t.hero.line3}
                <span className="text-[color:var(--accent)]">.</span>
              </motion.span>
            </motion.span>
          </h1>
        </div>

        {/* Footer row */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            custom={1}
            className="md:col-span-5 md:col-start-8 text-base md:text-lg leading-relaxed text-[color:var(--muted)] max-w-md"
          >
            <span className="font-serif-italic text-[color:var(--fg)]">HK Brand Lab</span> —{" "}
            {t.hero.tagline}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            custom={2}
            className="md:col-span-3 md:col-start-1 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-[color:var(--muted)]"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--accent)] animate-pulse" />
            {t.hero.scroll}
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
